import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { UserRoundCog, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from './auth.store';

/**
 * The way back.
 *
 * While impersonating, the signed-in profile is the *other* person's — their
 * name in the avatar menu, their roles, their stores. The Impersonation page
 * itself is behind a SuperAdmin gate the admin no longer passes, so there is
 * no route back through the app. This bar is the only exit, which is why it
 * sits above everything and does not scroll away.
 *
 * The original put a small "Stop User Impersonation" button after the footer,
 * below the fold on every page.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const impersonating = useAuthStore((state) => state.impersonating);
  const stop = useAuthStore((state) => state.stopImpersonation);
  const profile = useAuthStore((state) => state.profile);

  if (!impersonating) return null;

  const who =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    profile?.email ||
    '';

  return (
    <div
      role="status"
      className="bg-warning/20 text-foreground border-warning/40 flex shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm"
    >
      <UserRoundCog className="text-warning size-4 shrink-0" />
      <span className="text-pretty">
        {t('impersonation.bannerViewingAs', { name: who })}{' '}
        <span className="text-muted-foreground">
          {t('impersonation.bannerSignedInAs', {
            name: impersonating.adminName ?? '',
          })}
        </span>
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          stop();
          // Everything cached was fetched as the other person — their orders,
          // their stores, their permissions. None of it is the admin's.
          queryClient.clear();
          navigate('/impersonation');
        }}
      >
        <X className="size-3.5" />
        {t('impersonation.stop')}
      </Button>
    </div>
  );
}
